import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  computed,
  effect,
  inject,
} from '@angular/core';

import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';

import {
  ServerStatusService,
  type ReachabilityProbe,
  type ServerStatusData,
  type StatsWindow,
} from './server-status.service';

// Tree-shakable chart.js v4 — only register the controllers/elements/scales we use.
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
);

@Component({
  selector: 'sfo-status',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss'],
  standalone: false,
})
export class StatusComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly status = inject(ServerStatusService);

  readonly data = this.status.data;
  readonly error = this.status.error;
  readonly loading = this.status.loading;
  readonly isStartingUp = this.status.isStartingUp;
  readonly authRequired = this.status.authRequired;

  readonly title = computed(() => {
    const t = this.data()?.instance.title;
    return t ? `${t} server status` : 'Server status';
  });

  readonly windows = computed(() => {
    const d = this.data();
    if (!d) return [] as StatsWindow[];
    return [d.stats.last1h, d.stats.last24h];
  });

  /** Bar chart height grows with number of stages so labels never overlap. */
  readonly stageChartHeightPx = computed(() => {
    const n = this.data()?.stats.last24h.byStageP50Ms.length ?? 0;
    return Math.max(180, n * 32 + 80);
  });

  /**
   * Plain-language explanations for the technical reason codes returned by
   * the readiness probe. Keys must match `responsiveness-monitor.ts` exactly.
   * Anything not in this map falls back to the raw code (which is at least
   * informative even if not friendly).
   */
  private readonly reasonExplanations: Record<string, { title: string; detail: string }> = {
    'memory-pressure': {
      title: "We're running low on memory",
      detail:
        'The server is using a large fraction of the memory it has available. ' +
        'This often happens when many large documents are being processed at once. ' +
        'New analyses may be slower or fail until pressure eases.',
    },
    'queue-overloaded': {
      title: 'Too many documents in line',
      detail:
        "The analysis queue has grown beyond what we can process quickly. " +
        "Users uploading documents now will see a long wait. Consider increasing " +
        "the worker concurrency cap or waiting for the backlog to clear.",
    },
    'event-loop-stalled': {
      title: 'The server is overloaded right now',
      detail:
        "The main JavaScript thread is taking a long time to respond — usually " +
        "because it's busy parsing a large document, rendering a PDF, or doing " +
        "another heavy synchronous task. New requests may feel sluggish until it catches up.",
    },
  };

  /** Returns the friendly explanation for a readiness reason, or a generic fallback. */
  explainReason(code: string): { title: string; detail: string } {
    return (
      this.reasonExplanations[code] ?? {
        title: code,
        detail: 'A health check reported a problem with this signal.',
      }
    );
  }

  /**
   * Plain-language overview rendered at the top of the page for non-technical
   * readers. Three parts: a headline (one short sentence on overall state),
   * a "what's happening right now" line, and a "what happened in the last
   * hour" line. The technical cards below are unchanged.
   */
  readonly summary = computed(() => {
    const d = this.data();
    if (!d) return null;

    const healthy = d.overall === 'ok';
    const headline = healthy
      ? 'Everything is running smoothly.'
      : 'Something needs attention.';

    const queueActive = d.queue.active;
    const queueQueued = d.queue.queued;
    let currentActivity: string;
    if (queueActive === 0 && queueQueued === 0) {
      currentActivity = 'No documents are being analyzed right now.';
    } else if (queueQueued === 0) {
      currentActivity = `${queueActive} ${queueActive === 1 ? 'document is' : 'documents are'} being analyzed right now.`;
    } else {
      currentActivity = `${queueActive} ${queueActive === 1 ? 'document is' : 'documents are'} being analyzed; ${queueQueued} waiting in line.`;
    }

    const w = d.stats.last1h;
    let recentActivity: string;
    if (w.total === 0) {
      recentActivity = 'No documents have been processed in the last hour.';
    } else {
      const failed = w.failed;
      const succeeded = w.succeeded;
      if (failed === 0) {
        recentActivity = `In the last hour, ${w.total} ${w.total === 1 ? 'document was' : 'documents were'} processed successfully.`;
      } else {
        recentActivity = `In the last hour, ${succeeded} ${succeeded === 1 ? 'document' : 'documents'} succeeded and ${failed} failed.`;
      }
    }

    return {
      healthy,
      headline,
      currentActivity,
      recentActivity,
    };
  });

  @ViewChild('stageLatencyChart', { static: false }) stageLatencyCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('memoryChart', { static: false }) memoryCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('queueChart', { static: false }) queueCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChildren('activityChart') activityCanvases?: QueryList<ElementRef<HTMLCanvasElement>>;

  private stageLatencyChart?: Chart;
  private memoryChart?: Chart;
  private queueChart?: Chart;
  private activityCharts: Chart[] = [];
  private viewReady = false;

  // Re-render charts whenever the dashboard data changes. effect() runs in
  // injection context (field initializer is one), and we defer the actual
  // chart draw via queueMicrotask so the canvas elements have been laid out
  // by Angular's renderer before we touch them.
  private readonly chartSync = effect(() => {
    const data = this.data();
    if (!data || !this.viewReady) return;
    queueMicrotask(() => this.renderCharts(data));
  });

  ngOnInit() {
    this.status.subscribe(5_000);
  }

  ngAfterViewInit() {
    this.viewReady = true;
    const data = this.data();
    if (data) {
      queueMicrotask(() => this.renderCharts(data));
    }
  }

  ngOnDestroy() {
    this.status.unsubscribe();
    this.stageLatencyChart?.destroy();
    this.memoryChart?.destroy();
    this.queueChart?.destroy();
    this.activityCharts.forEach((c) => c.destroy());
    this.activityCharts = [];
  }

  /**
   * Reachability rows show a green/red dot and a human-readable line — never
   * the raw HTTP status code (any response, including 401 / 405, proves the
   * network path works, and showing those codes reads as "broken" to humans).
   * For filesystem-backed storage we surface free disk space instead.
   */
  reachabilitySummary(probe: ReachabilityProbe): string {
    if (probe.error === 'skipped (not configured)') {
      return 'skipped — not configured';
    }
    if (probe.kind === 'filesystem') {
      if (probe.freeBytes != null) {
        const mb = probe.freeBytes / 1024 / 1024;
        const formatted = mb > 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
        if (!probe.ok) return `${probe.error ?? 'low disk'}`;
        return `${formatted} free`;
      }
      return probe.ok ? 'available' : `unavailable: ${probe.error ?? 'unknown error'}`;
    }
    if (probe.ok) {
      return probe.latencyMs != null ? `reachable, ${probe.latencyMs} ms` : 'reachable';
    }
    return `unreachable: ${probe.error ?? 'unknown error'}`;
  }

  reachabilityState(probe: ReachabilityProbe): 'ok' | 'bad' | 'skipped' {
    if (probe.error === 'skipped (not configured)') return 'skipped';
    return probe.ok ? 'ok' : 'bad';
  }

  formatPercent(value: number): string {
    return `${(value * 100).toFixed(1)}%`;
  }

  trackByStage = (_: number, row: { stage: string }) => row.stage;
  trackByProbe = (_: number, row: ReachabilityProbe) => row.id;

  private renderCharts(data: ServerStatusData) {
    this.renderStageLatency(data.stats.last24h.byStageP50Ms);
    this.renderActivity(data);
    this.renderMemory(data);
    this.renderQueue(data);
  }

  /**
   * Horizontal stacked bar that shows where our memory is going relative to
   * the V8 hard limit. Three segments: app (JS heap), native (C++), and the
   * remaining "free room" until OOM. Colors shift the "free" segment to red
   * once we cross 85% used.
   */
  private renderMemory(data: ServerStatusData) {
    const canvas = this.memoryCanvas?.nativeElement;
    if (!canvas) return;
    const r = data.resources;
    const limit = r.heapLimitMb || 1;
    const app = r.heapUsedMb;
    const native = r.externalMb;
    const free = Math.max(0, limit - app - native);
    const usedFraction = (app + native) / limit;
    const freeColor = usedFraction > 0.85 ? '#fecaca' : usedFraction > 0.6 ? '#fde68a' : '#e5e7eb';

    const labels = [''];
    const datasets = [
      { label: 'App memory (JS heap)', data: [app], backgroundColor: '#3b82f6', stack: 'mem', borderRadius: 0, borderWidth: 0 },
      { label: 'Native libraries (C++)', data: [native], backgroundColor: '#8b5cf6', stack: 'mem', borderRadius: 0, borderWidth: 0 },
      { label: `Free room`, data: [free], backgroundColor: freeColor, stack: 'mem', borderRadius: 0, borderWidth: 0 },
    ];

    if (!this.memoryChart) {
      this.memoryChart = new Chart(canvas, {
        type: 'bar',
        data: { labels, datasets },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
            tooltip: {
              callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x} MB` },
            },
          },
          scales: {
            x: {
              stacked: true,
              max: limit,
              title: { display: true, text: `MB (limit: ${limit})`, font: { size: 11 } },
              grid: { color: '#f0f1f3' },
            },
            y: { stacked: true, display: false },
          },
        },
      });
    } else {
      this.memoryChart.data.datasets[0].data = [app];
      this.memoryChart.data.datasets[1].data = [native];
      this.memoryChart.data.datasets[2].data = [free];
      (this.memoryChart.data.datasets[2] as any).backgroundColor = freeColor;
      const xScale = this.memoryChart.options.scales!.x! as any;
      xScale.max = limit;
      xScale.title.text = `MB (limit: ${limit})`;
      this.memoryChart.update('none');
    }
  }

  /**
   * Donut showing how full the analysis queue is right now: active workers vs
   * free slots, with queued jobs as a separate wedge. Total ring size = the
   * concurrency cap; queued jobs extend "beyond" the ring conceptually but
   * here we show them as a second slice for compactness.
   */
  private renderQueue(data: ServerStatusData) {
    const canvas = this.queueCanvas?.nativeElement;
    if (!canvas) return;
    const q = data.queue;
    const free = Math.max(0, q.concurrency - q.active);
    const slices: { label: string; value: number; color: string }[] = [
      { label: 'Running', value: q.active, color: '#22c55e' },
      { label: 'Free slots', value: free, color: '#e5e7eb' },
    ];
    if (q.queued > 0) {
      slices.push({ label: 'Waiting', value: q.queued, color: '#f97316' });
    }
    const labels = slices.map((s) => s.label);
    const values = slices.map((s) => s.value);
    const colors = slices.map((s) => s.color);

    if (!this.queueChart) {
      this.queueChart = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{ data: values, backgroundColor: colors, borderWidth: 0 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: {
            // The adjacent kv list already shows the breakdown — a legend here
            // just fights with the centered "active/cap" label inside the hole.
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.label}: ${ctx.parsed}`,
              },
            },
          },
        },
      });
    } else {
      this.queueChart.data.labels = labels;
      this.queueChart.data.datasets[0].data = values;
      this.queueChart.data.datasets[0].backgroundColor = colors;
      this.queueChart.update('none');
    }
  }

  private renderStageLatency(rows: ServerStatusData['stats']['last24h']['byStageP50Ms']) {
    const canvas = this.stageLatencyCanvas?.nativeElement;
    if (!canvas) return;
    if (rows.length === 0) {
      this.stageLatencyChart?.destroy();
      this.stageLatencyChart = undefined;
      return;
    }
    const labels = rows.map((r) => r.stage);
    const p50 = rows.map((r) => r.p50);
    const p95 = rows.map((r) => r.p95);

    if (!this.stageLatencyChart) {
      this.stageLatencyChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Typical (p50)',
              data: p50,
              backgroundColor: '#60a5fa',
              borderRadius: 4,
            },
            {
              label: 'Slow case (p95)',
              data: p95,
              backgroundColor: '#1e3a8a',
              borderRadius: 4,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x} ms`,
              },
            },
          },
          scales: {
            x: {
              title: { display: true, text: 'milliseconds', font: { size: 11 } },
              grid: { color: '#f0f1f3' },
            },
            y: { grid: { display: false } },
          },
        },
      });
    } else {
      this.stageLatencyChart.data.labels = labels;
      this.stageLatencyChart.data.datasets[0].data = p50;
      this.stageLatencyChart.data.datasets[1].data = p95;
      this.stageLatencyChart.update('none');
    }
  }

  private renderActivity(data: ServerStatusData) {
    const canvases = this.activityCanvases?.toArray() ?? [];
    const windowsData = [data.stats.last1h, data.stats.last24h];
    canvases.forEach((ref, idx) => {
      const canvas = ref.nativeElement;
      const w = windowsData[idx];
      if (!w) return;

      // If everything is 0 we render a single grey "no activity" slice so the
      // donut still appears and signals scale, rather than disappearing.
      const empty = w.total === 0;
      const dataset = empty
        ? {
            data: [1],
            backgroundColor: ['#e5e7eb'],
            labels: ['no activity'],
          }
        : {
            data: [w.imports, w.aiAnalyses],
            backgroundColor: ['#60a5fa', '#22c55e'],
            labels: ['Imports', 'AI analyses'],
          };

      const existing = this.activityCharts[idx];
      if (!existing) {
        this.activityCharts[idx] = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: dataset.labels,
            datasets: [
              {
                data: dataset.data,
                backgroundColor: dataset.backgroundColor,
                borderWidth: 0,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '70%',
            plugins: {
              // Hide the chart-internal legend — the kv list next to the donut
              // already lists "Document imports / AI analyses", and the legend
              // would push the donut up inside its container and overlap the
              // absolute-positioned "TOTAL" label centered in the hole.
              legend: { display: false },
              tooltip: { enabled: !empty },
            },
          },
        });
      } else {
        existing.data.labels = dataset.labels;
        existing.data.datasets[0].data = dataset.data;
        existing.data.datasets[0].backgroundColor = dataset.backgroundColor;
        if (existing.options!.plugins!.tooltip) {
          (existing.options!.plugins!.tooltip as any).enabled = !empty;
        }
        existing.update('none');
      }
    });
  }
}
